// Centralised group cache-invalidation. Group mutations (join, leave,
// update, archive, role change, broadcast publish) invalidate the same
// constellation of keys — viewer access, member list, detail caches, the
// "mine" filter view, the broadcast scope under that group. A viewer's own
// membership change (join / leave) additionally invalidates the post feed,
// because membership is what the feed's read gate turns on. Before this
// module, 8+ mutation hooks each spelled out their own invalidate calls;
// a change to "what does a join invalidate?" required touching every site.
//
// Now: every group mutation's `onSettled` calls one of the helpers below.
// The function knows which keys to touch based on the `cause` discriminator,
// and which kind-specific keys to touch based on `kind`.

import type { QueryClient } from '@tanstack/react-query';
import { broadcastKeys } from '../queries/broadcasts';
import { feedKeys } from '../queries/feed';
import { groupKeys } from '../queries/groups';
import { identityKeys } from '../queries/identity';
import { leaderActionsKeys } from '../queries/leader-actions';
import { miscKeys } from '../queries/misc';
import { spacesKeys } from '../queries/spaces';
import { invalidateAndRefetchAll } from '../query-cache-utils';

export type GroupKind = 'circle' | 'network' | 'space';

export type GroupMutationCause =
  // viewer joined / left / withdrew — flips viewer-state widely
  | 'join'
  | 'leave'
  // group's own metadata changed (name, image, tone, etc.)
  | 'update'
  // archive / restore: invalidate across all kinds (id-only resolves server-side)
  | 'archive'
  // membership-management: role change, remove member, leadership transfer
  | 'members'
  // join-request approval (leader-side): the request leaves the queue and a
  // member joins
  | 'request-decision'
  // join-request decline (leader-side): only the queue changes
  | 'request-decline'
  // broadcast write — invalidates the broadcast scope under this group
  | 'broadcast';

// Whether a cause changes the viewer's leader action center: which boards
// they have (leave, archive/restore, a role change or removal) or what a
// board shows (its group's fields, its request queue). Exhaustive, so a new
// cause has to say.
const CHANGES_LEADER_ACTIONS: Readonly<Record<GroupMutationCause, boolean>> = {
  join: false,
  leave: true,
  update: true,
  archive: true,
  members: true,
  'request-decision': true,
  'request-decline': true,
  broadcast: false,
};

/**
 * Invalidate every cached key that a group mutation could have affected.
 *
 * Call from a mutation's `onSettled` once: it dispatches on `cause` to the
 * right key set. Callers that mutate cross-kind state (e.g. unknown-kind
 * archive) pass the kind they have; the archive case fans out across all
 * three kinds because the server resolves kind from id.
 *
 * The leader action center is decided here, once, for every cause: Spaces
 * have no board, so a mutation the caller knows is on a Space leaves it
 * alone; the kind-agnostic callers (archive, member management) pass
 * 'circle' and so always refresh it.
 */
export function invalidateGroupAfterMutation(
  qc: QueryClient,
  kind: GroupKind,
  groupId: string,
  cause: GroupMutationCause,
): void {
  invalidateGroupKeys(qc, kind, groupId, cause);
  if (kind !== 'space' && CHANGES_LEADER_ACTIONS[cause]) invalidateLeaderActions(qc);
}

function invalidateGroupKeys(
  qc: QueryClient,
  kind: GroupKind,
  groupId: string,
  cause: GroupMutationCause,
): void {
  switch (cause) {
    case 'join':
    case 'leave': {
      qc.invalidateQueries({ queryKey: groupKeys.viewerAccess(groupId).queryKey });
      invalidateMembersList(qc, groupId);
      invalidateDetailForKind(qc, kind, groupId);
      // "My X" filter view is usually inactive when a join/leave fires
      // (user is browsing all-X). Default invalidate marks it stale but
      // doesn't refetch — the next tab switch would flash stale rows.
      invalidateMineForKind(qc, kind);
      // Membership is the feed's read gate: joining reveals the group's posts,
      // leaving hides them. Refresh the feed the same way a new post does so
      // the joined member sees the historical + pinned posts immediately
      // instead of the stale pre-join feed (issue #162).
      invalidateViewerFeed(qc);
      // Requesting to join adds a row to the directory's "Pending Requests"
      // section; joining an open group / leaving never adds one, so this is a
      // no-op refetch there. Keyed once — the read is kind-agnostic.
      qc.invalidateQueries({ queryKey: groupKeys.myPendingRequests().queryKey });
      // The server ranks All Circles on memberships too: a Circle the member
      // is in or has a live request for leaves the exploration pool, and
      // direct Network membership feeds the personalized Network term.
      if (kind !== 'space') markCircleRankingStale(qc);
      if (kind === 'circle') invalidateCirclePreferencesAsk(qc);
      return;
    }
    case 'update': {
      invalidateDetailForKind(qc, kind, groupId);
      invalidateListsForKind(qc, kind);
      // The viewer's own lists render the same name, description and photo.
      invalidateMineForKind(qc, kind);
      // The leader getting-started steps ("Add a photo and description to
      // your Circle", "Update your Network page") are derived from the
      // group's own fields. Spaces have none.
      if (kind !== 'space') {
        qc.invalidateQueries({ queryKey: miscKeys.onboardingProgress().queryKey });
      }
      return;
    }
    case 'archive': {
      // Archive endpoints are kind-agnostic on the server side — we don't
      // always know which kind a group is at hook-call time. Fan out across
      // every kind so whichever cache held the row gets invalidated.
      qc.invalidateQueries({ queryKey: groupKeys.circle.detail(groupId).queryKey });
      qc.invalidateQueries({ queryKey: groupKeys.circle.lists() });
      qc.invalidateQueries({ queryKey: groupKeys.network.detail(groupId).queryKey });
      qc.invalidateQueries({ queryKey: groupKeys.network.lists() });
      qc.invalidateQueries({ queryKey: spacesKeys.detail(groupId).queryKey });
      qc.invalidateQueries({ queryKey: spacesKeys.lists() });
      invalidateMineForKind(qc, 'circle');
      invalidateMineForKind(qc, 'network');
      // Archiving the viewer's only Circle leaves them in none; restoring it
      // puts them back in one.
      invalidateCirclePreferencesAsk(qc);
      return;
    }
    case 'members': {
      invalidateMembersList(qc, groupId);
      qc.invalidateQueries({ queryKey: groupKeys.viewerAccess(groupId).queryKey });
      // We don't know the kind at the role/remove endpoint (it's kind-
      // agnostic on the server) — fan out detail invalidations across
      // every kind so whichever cache holds the row gets refreshed.
      qc.invalidateQueries({ queryKey: groupKeys.circle.detail(groupId).queryKey });
      qc.invalidateQueries({ queryKey: groupKeys.network.detail(groupId).queryKey });
      qc.invalidateQueries({ queryKey: groupKeys.network.leaders(groupId).queryKey });
      qc.invalidateQueries({ queryKey: spacesKeys.detail(groupId).queryKey });
      return;
    }
    case 'request-decision': {
      qc.invalidateQueries({ queryKey: joinRequestsForKind(kind, groupId) });
      invalidateMembersList(qc, groupId);
      invalidateDetailForKind(qc, kind, groupId);
      return;
    }
    case 'request-decline': {
      // A decline changes no members or detail: only the narrow per-kind
      // requests key.
      qc.invalidateQueries({ queryKey: joinRequestsForKind(kind, groupId) });
      return;
    }
    case 'broadcast': {
      const scope = {
        kind: 'group' as const,
        groupKind: kind,
        groupId,
      };
      qc.invalidateQueries({
        queryKey: [...broadcastKeys.scope(scope), 'list'] as const,
      });
      qc.invalidateQueries({ queryKey: broadcastKeys.statusCounts(scope).queryKey });
      return;
    }
  }
}

/** The viewer's leader action center: one board per group they lead. For
 *  mutations that change the viewer's boards or what they show and are not
 *  a group mutation `invalidateGroupAfterMutation` already decides (creating
 *  a group, accepting an invitation, an event, a post, reading a message). */
export function invalidateLeaderActions(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: leaderActionsKeys.me().queryKey });
}

/**
 * The directory asks for Circle preferences only while the member is in no
 * active Circle, so the ask is refetched after the viewer's own Circle
 * membership changes (a join, a leave, starting or archiving a Circle): the
 * card leaves the list, or comes back, without a reload. Accepting an
 * invitation refreshes every identity key, the ask's included.
 */
export function invalidateCirclePreferencesAsk(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: identityKeys.circlePreferences().queryKey });
}

/**
 * Re-rank the directory now, after the member changed what they declared
 * (Circle preferences, profile location, industry or locale): the ranked lists
 * on screen refetch in the new order. There is no client re-rank.
 */
export function invalidateCircleRanking(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: groupKeys.circle.lists() });
}

/**
 * Re-rank the directory on the member's next visit, after a membership change
 * the ranking reads: a Circle or Network join or leave, a Circle request or a
 * withdrawn Circle request. (A Network request is marked stale too, through
 * the shared join path; the ranking does not read it, so that costs one
 * unneeded refetch on the next visit.) The lists on screen are marked stale
 * but not refetched, so All Circles does not reorder under a member who is
 * browsing it; the card they acted on already shows its new state.
 */
export function markCircleRankingStale(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: groupKeys.circle.lists(), refetchType: 'none' });
}

/**
 * Invalidate the viewer's post feed after their own membership changed.
 *
 * A join/leave/invite-accept flips the feed's read gate, so every feed *list*
 * the viewer has cached can now be wrong: the group's own page
 * (`scope: 'group:<id>'`), the ambient home feed and kind:* feeds
 * (`feedVisible` = member-of-a-readable-scope), and — because child-circle
 * membership cascades to the parent network — that network's page too. We
 * invalidate the broad `feedKeys.lists()` prefix (partial-matches every scoped
 * list regardless of pageSize) plus the sidebar scope counts, mirroring how
 * `useCreateFeedPost` refreshes the feed when a post appears. Only the mounted
 * feed refetches immediately; the rest are marked stale for their next mount.
 */
export function invalidateViewerFeed(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: feedKeys.lists() });
  qc.invalidateQueries({ queryKey: feedKeys.scopeCounts().queryKey });
}

/**
 * Attaching / detaching a circle from a network mutates BOTH entities, so the
 * single-group `invalidateGroupAfterMutation` (keyed by one groupId) can't
 * express it. Covers all three ways the link changes — link, unlink, and
 * creating a circle already linked to a parent network. Refresh:
 *   - the network's child-circle list (admin panel + public detail roster),
 *     refetched even while inactive, and its detail (circle count)
 *   - the circle's detail (its parent network changed) + the circle lists
 *   - the viewer's own `circle.mine()` — REFETCHED, not just invalidated,
 *     because the /circles list is inactive when the link is made from the
 *     network admin panel and would otherwise stay stale until a reload.
 */
export function invalidateAfterCircleNetworkLink(
  qc: QueryClient,
  networkId: string,
  circleId: string,
): void {
  // `circlesAll` is the inner node the leaf composes, so this reaches every
  // arg variant (admin @50 includeArchived, public roster @24) without
  // respelling the key. REFETCHED, not just invalidated: on the create path the
  // circle is created from /circles/new and the admin list is unmounted, so a
  // default invalidate would only mark it stale and the screen it navigates to
  // would render the pre-create pages until the mount refetch settled.
  invalidateAndRefetchAll(qc, {
    queryKey: groupKeys.network.circlesAll(networkId),
  });
  invalidateDetailForKind(qc, 'network', networkId);
  invalidateDetailForKind(qc, 'circle', circleId);
  invalidateListsForKind(qc, 'circle');
  invalidateMineForKind(qc, 'circle');
  // The Network board's Circle count and the Circle board's Network task.
  invalidateLeaderActions(qc);
}

// The member-list key carries a trailing `{ search }` object so each filter
// caches separately. Invalidate by the opts-less prefix so a list that's
// currently filtered by a search term still refetches — an exact
// `members(id)` key builds `{ search: '' }` and would only match the
// unfiltered query, leaving a filtered roster stale until reload.
function invalidateMembersList(qc: QueryClient, groupId: string): void {
  qc.invalidateQueries({ queryKey: [...groupKeys.all, 'members', groupId] });
  // The leader-only roster is a separate leaf feeding the same admin table —
  // without this a leader who promotes or removes someone sees a stale row.
  qc.invalidateQueries({ queryKey: groupKeys.roster(groupId).queryKey });
}

function invalidateDetailForKind(
  qc: QueryClient,
  kind: GroupKind,
  groupId: string,
): void {
  if (kind === 'circle') {
    qc.invalidateQueries({ queryKey: groupKeys.circle.detail(groupId).queryKey });
    return;
  }
  if (kind === 'network') {
    qc.invalidateQueries({ queryKey: groupKeys.network.detail(groupId).queryKey });
    return;
  }
  qc.invalidateQueries({ queryKey: spacesKeys.detail(groupId).queryKey });
}

function invalidateListsForKind(qc: QueryClient, kind: GroupKind): void {
  if (kind === 'circle') {
    qc.invalidateQueries({ queryKey: groupKeys.circle.lists() });
    return;
  }
  if (kind === 'network') {
    qc.invalidateQueries({ queryKey: groupKeys.network.lists() });
    return;
  }
  qc.invalidateQueries({ queryKey: spacesKeys.lists() });
}

function invalidateMineForKind(qc: QueryClient, kind: GroupKind): void {
  if (kind === 'circle') {
    // Opts-less prefix: `circle.mine` is keyed by { includeArchived, pageSize,
    // filters }, so refetch every variant (page-6 preview, page-100 picker).
    invalidateAndRefetchAll(qc, {
      queryKey: [...groupKeys.circle.all, 'mine'],
    });
    return;
  }
  if (kind === 'network') {
    // Opts-less prefix: `network.mine` is keyed by { includeArchived, pageSize,
    // filters }, so refetch every variant (audience picker @100, browse @default).
    invalidateAndRefetchAll(qc, { queryKey: [...groupKeys.network.all, 'mine'] });
    return;
  }
  // Spaces don't surface a separate "mine" key; the lists() view holds it.
  // The directory's search results split by membership (My Spaces is the
  // viewer-scoped search), so they refresh with it.
  qc.invalidateQueries({ queryKey: spacesKeys.lists() });
  qc.invalidateQueries({ queryKey: spacesKeys.searches() });
}

function joinRequestsForKind(
  kind: GroupKind,
  groupId: string,
): readonly unknown[] {
  if (kind === 'circle') return groupKeys.circle.joinRequests(groupId).queryKey;
  if (kind === 'network') return groupKeys.network.joinRequests(groupId).queryKey;
  // Spaces don't support request-to-join. Falls through to a no-op key.
  return [...spacesKeys.detail(groupId).queryKey, 'join-requests'] as const;
}
